/**
 * `L` — ways in and out (§9.2 `portal.linking`, §4.3.1 `Portal`).
 *
 *  - **Click near a side of the picture** (within {@link EDGE_BAND_PCT}) → a way in/out on that
 *    edge: characters walk on and off there.
 *  - **Click a spot** (a door, a gate…) → a way in/out standing on that spot.
 *  - **Drag from a way-in/out marker to a spot** → attach it to that spot (the old linking gesture).
 *
 * On an edge the kind is the armed portal kind (`activeKind.portal`). On a spot the store picks: the
 * armed kind when it is one that stands on a spot, else a door-like kind from the live list.
 * Releasing a drag on empty canvas does nothing: an unlinked portal is legal (it "leads somewhere"
 * and the story decides), so this tool never destroys one.
 */
import type { PortalEdge } from '@/schema'

import { idle, none, withState, type ToolConfig, type ToolEffect, type ToolHandler, type ToolResult } from './types'

/** How close to a side of the picture a click must be to put a way in/out on that edge (percent). */
export const EDGE_BAND_PCT = 10

/** The picture edge a point is near, nearest first; null in the middle of the picture. */
export function edgeNear(at: readonly [number, number], band = EDGE_BAND_PCT): PortalEdge | null {
  const [x, y] = at
  const candidates: [PortalEdge, number][] = [
    ['left', x],
    ['right', 100 - x],
    ['bottom', 100 - y],
    ['top', y],
  ]
  const hit = candidates.filter(([, d]) => d <= band).sort((a, b) => a[1] - b[1])[0]
  return hit ? hit[0] : null
}

const create = (effect: ToolEffect): ToolResult => idle('portal', [{ k: 'beginGesture' }, effect, { k: 'endGesture' }])

function typeOf(cfg: ToolConfig | undefined, id: string): string | undefined {
  return cfg?.typeOf?.(id)
}

export const portalTool: ToolHandler = {
  id: 'portal',
  shortcut: 'l',
  icon: 'DoorOpen',
  label: 'Way in/out',
  hint: 'Click near a side of the picture, or on a door or gate, to make a way in and out.',
  cursor: 'crosshair',

  reduce(machine, ev, cfg): ToolResult | null {
    const st = machine.state

    if (ev.t === 'pointerdown' && ev.button === 0) {
      if (ev.hit.id) return withState('portal', { t: 'portal.linking', from_id: ev.hit.id, cursor: ev.at })
      const edge = edgeNear(ev.at)
      if (edge) return create({ k: 'createPortal', edge, ...(cfg?.activeKind.portal ? { kind: cfg.activeKind.portal } : {}) })
      return idle('portal', [
        { k: 'toast', message: 'Click near the left or right side of the picture, or on a door or gate, to make a way in and out.', tone: 'info' },
      ])
    }

    if (ev.t === 'pointermove') {
      if (st.t !== 'portal.linking') return none(machine)
      return withState('portal', { ...st, cursor: ev.at })
    }

    if (ev.t === 'pointerup') {
      if (st.t !== 'portal.linking') return none(machine)
      const target = ev.hit.id
      const fromType = typeOf(cfg, st.from_id)
      // Pressed and released on a spot: a way in/out standing on it.
      if (fromType === 'anchor') {
        if (target !== st.from_id) return idle('portal')
        // No kind here: the store keeps the armed one only if it is a kind that stands on a spot
        // (the default armed kind is "edge of the picture", which on a door means nothing).
        return create({ k: 'createPortal', anchor_id: st.from_id })
      }
      if (!target || target === st.from_id) return idle('portal')
      // Only a way in/out can be attached, and only to a spot (when the canvas says what things are).
      if (fromType !== undefined && fromType !== 'portal') return idle('portal')
      const targetType = typeOf(cfg, target)
      if (targetType !== undefined && targetType !== 'anchor') return idle('portal')
      return idle('portal', [{ k: 'beginGesture' }, { k: 'linkPortal', from_id: st.from_id, to_id: target }, { k: 'endGesture' }])
    }

    if (ev.t === 'cancel' || ev.t === 'pointercancel') return idle('portal')

    return null
  },
}
